import { MembershipStatusEnum, PermissionEnum } from '@sr-sdks/api-sdk-axios';
import { Button, Card, CardContent } from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import { useOutletContext } from 'react-router-dom';
import { queryOptionsGetMembership } from '../api/memberships/membershipApi';
import { queryOptionsGetWorkspace } from '../api/workspaces/workspaceApi';
import { isWorkspaceReadyForMembership } from '../forms/workspaceDetails/shared/membershipReadiness';
import { useCheckUserPermissions } from '../hooks/useCheckUserPermissions';
import { useEffectiveMembershipStatus } from '../hooks/useEffectiveMembershipStatus';
import { userService } from '../reducers/permissions/userSlice';
import { useTypedSelector } from '../stateStore';
import { WorkspaceOutletContext } from '../types/workspaceTypes';
import {
  isAllowedToApplyMembership,
  isExternalUser,
  toTitleCase,
} from '../utils/helpers/common';
import IfFeatureEnabled from './IfFeatureEnabled';
import { AppLoader } from './Loader';
import { MSR3Empty } from './MSR3Empty';
import { MembershipActionsMenu } from './MembershipActionsMenu';
import MembershipFeedback from './MembershipFeedback';
import ProtectElement from './ProtectElement';
import { ErrorFallback } from './QueryResults/ErrorFallback';
import { SingleQueryResults } from './QueryResults/SingleQueryResults';
import ViewModeField from './ViewModeField';

const LoadingFallback = (
  <section className="space-y-4">
    <h2 className="text-base font-medium text-neutral-950">
      Membership Details
    </h2>

    <Card className="rounded-lg border-neutral-300 shadow-none">
      <CardContent className="p-5">
        <AppLoader.FlexLoader loaderHeight="h-32" />
      </CardContent>
    </Card>
  </section>
);

type MembershipDetailsCardProps = {
  /** Flags the empty required fields in the Workspace Details form below. */
  onHighlightEmptyDetails: () => void;
};

export const MembershipDetailsCard = ({
  onHighlightEmptyDetails,
}: MembershipDetailsCardProps) => {
  const { workspaceId } = useOutletContext<WorkspaceOutletContext>();
  const currentUser = useTypedSelector(userService.selectors.userMe);
  const canApplyOnBehalfOfWorkspace = useCheckUserPermissions(
    [PermissionEnum.EditWorkspaceDetails],
    currentUser,
    workspaceId,
  );

  const workspaceQuery = useQuery(queryOptionsGetWorkspace(workspaceId));
  const membershipQuery = useQuery(
    queryOptionsGetMembership(workspaceQuery.data?.membershipId),
  );

  const hasMembershipQuery = Boolean(workspaceQuery.data?.membershipId);
  const isLoadingMembership = hasMembershipQuery && membershipQuery.isPending;

  const { isCurrentMember, membershipStatus, showMembershipStatus } =
    useEffectiveMembershipStatus(workspaceQuery.data, membershipQuery.data);

  return (
    <SingleQueryResults
      errorFallback={(error) => <ErrorFallback error={error} />}
      loadingFallback={LoadingFallback}
      query={workspaceQuery}
    >
      {(workspace) => {
        if (isLoadingMembership) return LoadingFallback;
        if (hasMembershipQuery && membershipQuery.isError) {
          return <ErrorFallback error={membershipQuery.error} />;
        }

        const membership = membershipQuery.data;

        const workspaceWithEffectiveMembershipStatus = {
          ...workspace,
          membershipStatus,
        };

        // Hide the in-line feedback (e.g. "in review" copy) when we override
        // the workspace status — the membership record outranks whatever copy
        // the stale workspace status would render.
        const showMembershipFeedback =
          membershipStatus === workspace.membershipStatus;

        // Wherever Apply could show (Add Title links here, with or without the
        // actions menu flag), plus staff-sent for the customer, whom
        // MembershipApplicationGate points here. Staff see the status they set.
        const couldApply = isAllowedToApplyMembership(
          workspaceWithEffectiveMembershipStatus,
          currentUser,
        );
        const isStaffSentToCustomer =
          membershipStatus === MembershipStatusEnum.ApplicationSent &&
          isExternalUser(currentUser);
        const isMissingApplicationDetails =
          canApplyOnBehalfOfWorkspace &&
          (couldApply || isStaffSentToCustomer) &&
          !isWorkspaceReadyForMembership(workspace);

        if (isMissingApplicationDetails) {
          return (
            <section className="space-y-4">
              <h2 className="text-base font-medium text-neutral-950">
                Membership Details
              </h2>

              <Card className="rounded-lg border-neutral-300 shadow-none">
                <CardContent className="p-5">
                  <MSR3Empty
                    title="Workspace Details Incomplete"
                    description="Please update your workspace details to apply for a membership"
                    iconProps={{ icon: 'info' }}
                  >
                    <Button onClick={onHighlightEmptyDetails}>
                      Highlight Empty Details
                    </Button>
                  </MSR3Empty>
                </CardContent>
              </Card>
            </section>
          );
        }

        return (
          <section className="space-y-4">
            <h2 className="text-base font-medium text-neutral-950">
              Membership Details
            </h2>

            <Card className="rounded-lg border-neutral-300 shadow-none">
              <CardContent className="space-y-5 p-5">
                <div className="space-y-4">
                  {!isCurrentMember && (
                    <p className="text-sm text-neutral-700">
                      Screenrights membership is required to collect secondary
                      royalties.
                    </p>
                  )}

                  {showMembershipStatus && (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <ViewModeField
                        title="Membership Status"
                        value={toTitleCase(membershipStatus)}
                      />

                      {isCurrentMember && membership?.memberId && (
                        <ViewModeField
                          title="Member ID"
                          value={membership.memberId}
                        />
                      )}
                    </div>
                  )}

                  {showMembershipFeedback && <MembershipFeedback />}
                </div>

                <IfFeatureEnabled name="membershipActionsMenu">
                  <ProtectElement
                    permissionRequired={[PermissionEnum.EditWorkspaceDetails]}
                    workspaceId={workspaceId}
                  >
                    <MembershipActionsMenu
                      className="flex-col sm:flex-row"
                      workspace={workspaceWithEffectiveMembershipStatus}
                    />
                  </ProtectElement>
                </IfFeatureEnabled>
              </CardContent>
            </Card>
          </section>
        );
      }}
    </SingleQueryResults>
  );
};
